import { HeaderLink } from "../CategoryHeaderProps";
import React, { Fragment, useContext, useMemo } from "react";
import { isEmpty } from "ramda";
import { isIE, isMobileDevice, KMS_GLOBAL, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { MeetingRoomHelper } from "@kaltura/mediaspace-shared-meeting-room";
import styled from "@emotion/styled";
import { Camera24Icon, Chat24Icon, ExternalLink24Icon } from "@kaltura/ds-react-icons";
import { Button, IconButton, Tooltip } from "@kaltura/ds-react-components";
import { Subscribe, SubscribeType } from "@kaltura/mediaspace-shared-subscribe";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import clsx from "clsx";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface LinksProps {
    newRowLink?: string;
    newRowText?: string;
    newRowClass?: string;
    newRowDisabled?: boolean;
    newrowOpenNewTab?: boolean;
    externalLink?: string;
    externalLinkLabel?: string;
    externalLink2?: string;
    externalLinkLabel2?: string;
    shouldShowChatLink?: boolean;
    safariSupport?: boolean;
    mobileSupport?: boolean;
    isAdvancedTemplateRoom?: boolean;
    subscribe?: SubscribeType;
    analyticsValue?: string;
}

const StyledLinks = styled("div")(({ theme }) => ({
    display: "flex",
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(2),
    flexWrap: "wrap",
}));

/**
 * Channel page header link buttons
 */
export function Links(props: LinksProps) {
    const { subscribe } = props;

    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const sendButtonAnalytics = useButtonAnalytics();

    const links = useMemo(() => {
        const {
            safariSupport = false,
            mobileSupport = false,
            isAdvancedTemplateRoom = false,
            newRowLink = "",
            newrowOpenNewTab = false,
            newRowText = "",
            newRowClass = "",
            newRowDisabled = false,
            externalLink = "",
            externalLinkLabel = "",
            externalLink2 = "",
            externalLinkLabel2 = "",
            shouldShowChatLink = false,
            analyticsValue,
        } = props;

        const links: HeaderLink[] = [];

        // Safari is guaranteed to be supported by rooms with advanced templates
        const isNewRowDisabled =
            newRowDisabled
            || !MeetingRoomHelper.isNewrowSupportedInCurrentBrowser(safariSupport || isAdvancedTemplateRoom);

        if (newRowLink && (!isMobileDevice || mobileSupport || isAdvancedTemplateRoom)) {
            links.push({
                title: !isEmpty(newRowText) ? newRowText : translate("Let's Meet"),
                link: newRowLink,
                icon: <Camera24Icon />,
                newTab: newrowOpenNewTab && !isAdvancedTemplateRoom,
                className: clsx(newRowClass, "kms-ds-channel-page-header-newrow-button"),
                color: "primary",
                disabled: isNewRowDisabled,
                onClick: () => {
                    if (analyticsValue) {
                        sendButtonAnalytics(analyticsValue, ButtonClickAnalyticsType.LAUNCH);
                    }
                },
            });
        }
        if (shouldShowChatLink && !isIE) {
            links.push({
                title: translate("Let's Chat"),
                link: "",
                onClick: (event) => {
                    event.preventDefault();
                    sendButtonAnalytics("Channel Call To Action", ButtonClickAnalyticsType.LAUNCH);
                    KMS_GLOBAL?.cncWidget?.startChatWithModerator();
                },
                icon: <Chat24Icon />,
                className: "kms-ds-channel-page-header-chat-button",
            });
        }
        if (externalLink && externalLinkLabel) {
            links.push({
                title: externalLinkLabel,
                link: externalLink,
                icon: <ExternalLink24Icon />,
                newTab: true,
                onClick: () => sendButtonAnalytics("Channel Call To Action", ButtonClickAnalyticsType.NAVIGATE),
                className: "kms-ds-channel-page-header-external-link-1-button",
                externalLink: true,
            });
        }
        if (externalLink2 && externalLinkLabel2) {
            links.push({
                title: externalLinkLabel2,
                link: externalLink2,
                icon: <ExternalLink24Icon />,
                newTab: true,
                onClick: () => sendButtonAnalytics("Channel Call To Action", ButtonClickAnalyticsType.NAVIGATE),
                className: "kms-ds-channel-page-header-external-link-2-button",
                externalLink: true,
            });
        }

        // show max 3 buttons (also on mobile)
        return links.slice(0, 3);
    }, [props, sendButtonAnalytics]);
    const hasTwoExternalLinks = props.externalLink && props.externalLink2;

    return (
        <StyledLinks className={"kms-ds-channel-page-header-buttons"}>
            {links.map((link: HeaderLink, index: number) => {
                const {
                    className,
                    link: url,
                    onClick,
                    newTab,
                    icon,
                    title,
                    color = "translucent",
                    disabled,
                    externalLink,
                } = link;
                // external links (CTA) should show as icon buttons (no text) on kaf mobile when both CTAs are populated
                const useIconButton = externalLink && isKaf && isSmallScreen && hasTwoExternalLinks;
                return (
                    <Fragment key={index}>
                        {useIconButton && (
                            <Tooltip title={title} placement="top">
                                <IconButton
                                    key={index}
                                    color={color}
                                    className={`channel-header__link ${className}`}
                                    variant="pill"
                                    onClick={(e) => {
                                        onClick?.(e);
                                        window.open(url, newTab ? "_blank" : "_self");
                                    }}
                                    disabled={disabled}
                                >
                                    {<ExternalLink24Icon />}
                                </IconButton>
                            </Tooltip>
                        )}
                        {!useIconButton && (
                            <Button
                                key={index}
                                color={color}
                                className={`channel-header__link ${className}`}
                                href={url}
                                onClick={onClick}
                                target={newTab ? "_blank" : "_self"}
                                startIcon={icon}
                                disabled={disabled}
                            >
                                {title}
                            </Button>
                        )}
                    </Fragment>
                );
            })}
            {subscribe && <Subscribe {...subscribe} linksCount={links.length} />}
        </StyledLinks>
    );
}

export default Links;
